import React from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { Typography, Paper, Box, Avatar } from "@mui/material";
import {
  Create as CreateIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  SwapHoriz as SwapHorizIcon,
  Message as MessageIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const TicketHistory = ({ history }) => {
  // Helper to safely format dates
  const safeFormatDate = (dateValue, formatStr) => {
    if (!dateValue) return "-";
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "-";
      return format(date, formatStr);
    } catch (error) {
      console.error("Error formatting date:", dateValue, error);
      return "-";
    }
  };

  // Icon mapping for different actions
  const getActionIcon = (action) => {
    switch (action) {
      case "created":
        return <CreateIcon />;
      case "status_changed":
        return <EditIcon />;
      case "priority_changed":
        return <EditIcon />;
      case "assigned":
        return <PersonAddIcon />;
      case "transferred":
        return <SwapHorizIcon />;
      case "message_added":
        return <MessageIcon />;
      case "closed":
        return <CheckIcon />;
      default:
        return <EditIcon />;
    }
  };

  // Color mapping for different actions
  const getActionColor = (action) => {
    switch (action) {
      case "created":
        return "success";
      case "status_changed":
        return "info";
      case "priority_changed":
        return "warning";
      case "assigned":
      case "transferred":
        return "primary";
      case "message_added":
        return "default";
      case "closed":
        return "error";
      default:
        return "grey";
    }
  };

  // Format action text
  const getActionText = (historyItem) => {
    const { action, oldValue, newValue, details } = historyItem;

    switch (action) {
      case "created":
        return "Ticket created";
      case "status_changed":
        return `Status changed from ${oldValue} to ${newValue}`;
      case "priority_changed":
        return `Priority changed from ${oldValue} to ${newValue}`;
      case "assigned":
        return `Ticket assigned to ${newValue}`;
      case "transferred":
        return `Ticket transferred from ${oldValue} to ${newValue}`;
      case "message_added":
        return "Message added";
      case "closed":
        return "Ticket closed";
      default:
        return details || action;
    }
  };

  if (!history || history.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          No history available
        </Typography>
      </Box>
    );
  }

  // Sort history by timestamp (newest first)
  const sortedHistory = [...history].sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);

    // Handle invalid dates
    if (isNaN(dateA.getTime())) return 1;
    if (isNaN(dateB.getTime())) return -1;

    return dateB - dateA;
  });

  return (
    <Timeline position="right">
      {sortedHistory.map((item, index) => (
        <TimelineItem key={item.id || index}>
          <TimelineOppositeContent
            color="text.secondary"
            sx={{ flex: 0.3, paddingLeft: 0 }}
          >
            <Typography variant="caption" display="block">
              {safeFormatDate(item.timestamp, "MMM dd, yyyy")}
            </Typography>
            <Typography variant="caption" display="block">
              {safeFormatDate(item.timestamp, "HH:mm")}
            </Typography>
          </TimelineOppositeContent>

          <TimelineSeparator>
            <TimelineDot color={getActionColor(item.action)} variant="outlined">
              {getActionIcon(item.action)}
            </TimelineDot>
            {index < sortedHistory.length - 1 && <TimelineConnector />}
          </TimelineSeparator>

          <TimelineContent>
            <Paper elevation={0} sx={{ p: 2, bgcolor: "background.default" }}>
              <Typography variant="body2" fontWeight={500}>
                {getActionText(item)}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                {item.performedBy?.avatar && (
                  <Avatar
                    src={item.performedBy.avatar}
                    alt={item.performedByName}
                    sx={{ width: 20, height: 20 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  by {item.performedByName || "System"}
                </Typography>
              </Box>
              {item.details && item.action !== "message_added" && (
                <Typography
                  variant="caption"
                  display="block"
                  mt={1}
                  color="text.secondary"
                >
                  {item.details}
                </Typography>
              )}
            </Paper>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
};

export default TicketHistory;
